import { CreateTeamUserRequest, GetUserResponse } from '@/api';
import { useCallback, useEffect, useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useOpenApi } from '@/common/providers/open-api-provider';
import { getFallbackErrorKey, isApiError } from '@/common/util';
import { UIToast } from '@/ui';
import { useCompaniesTranslation } from '../hooks';
import { addOrUpdateCompanyUsersCache } from '../queries/company-cache';

/** The member to add, and the team plus roles to add them with. */
type AddTeamUserVariables = {
    companyUserId: string;
    data: CreateTeamUserRequest;
};

type UseAddTeamUserActionData = {
    addTeamUser: (
        companyUserId: string,
        data: CreateTeamUserRequest
    ) => Promise<GetUserResponse>;
    loading: boolean;
    error: string;
};

/**
 * Adding a member to a team.
 *
 * Carries no `isAllowed` of its own: both callers are pickers — one choosing
 * members for a team, the other teams for a member — so neither the member nor
 * the team is known until the row is clicked. The API refuses what the viewer
 * may not do, and the refusal is spelled out here.
 */
export function useAddTeamUserAction(): UseAddTeamUserActionData {
    const { t } = useCompaniesTranslation();
    const { openApi } = useOpenApi();
    const openApiRef = useRef(openApi);
    useEffect(() => {
        openApiRef.current = openApi;
    });

    const queryClient = useQueryClient();

    // One place that turns a failure into a message, so the toast and the
    // returned `error` can never disagree. Every error
    // `POST /v3/users/{id}/teams` declares gets a branch.
    const getErrorMessage = useCallback(
        (error: unknown): string => {
            if (isApiError(error, 'USER_ALREADY_IN_TEAM')) {
                return t('actions.add_team_user.error_already_in_team');
            }
            if (isApiError(error, 'COMPANY_USER_NOT_FOUND')) {
                return t('actions.add_team_user.error_user_not_found');
            }
            if (isApiError(error, 'TEAM_NOT_FOUND')) {
                return t('actions.add_team_user.error_team_not_found');
            }
            if (isApiError(error, 'FORBIDDEN')) {
                return t('actions.add_team_user.error_not_permitted');
            }
            if (isApiError(error, 'BAD_REQUEST')) {
                return error.response?.data.message.join(', ') ?? '';
            }

            return t(getFallbackErrorKey(error));
        },
        [t]
    );

    const mutation = useMutation({
        mutationFn: ({ companyUserId, data }: AddTeamUserVariables) =>
            openApiRef.current.users
                .addUserToTeam(companyUserId, data)
                .then((response) => response.data),
        onSuccess: (data) => {
            addOrUpdateCompanyUsersCache(queryClient, data.data.user);

            UIToast.success(t('actions.add_team_user.success'));
        },
        onError: (error) => {
            UIToast.error(getErrorMessage(error));
        }
    });

    return {
        addTeamUser: (
            companyUserId: string,
            data: CreateTeamUserRequest
        ): Promise<GetUserResponse> =>
            mutation.mutateAsync({ companyUserId, data }),
        loading: mutation.isPending,
        error: mutation.error ? getErrorMessage(mutation.error) : ''
    };
}
